import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { AtlasObjectType, IAtlasObjectRef, toAtlasRouterPath } from '@common/helpers/atlasUrls'
import { atlasObjectRefKey } from '@common/interfaces/atlasObjects'
import { ROUTES } from '../constants/routes'
import { atlasObjectPreviews } from '../redux/atlasLinks/selectors'
import { atlasModulePanelTarget } from '../redux/atlasModulePanel/selectors'
import {
  IAtlasPreviewTarget,
  closeAtlasPreview,
  getAtlasObjectDetail,
  openAtlasPreview,
} from '../redux/atlasPreview/actions'
import { useDispatch } from '../redux/hooks'
import { IRootState } from '../redux/reducer'
import { anchorPreview, clearPreviewAnchor } from './atlasPreviewAnchor'
import { useAtlasModulePanel } from './useAtlasModulePanel'

const isTaskPanelHost = (pathname: string): boolean =>
  !!matchPath(ROUTES.MY_TASKS, pathname) || !!matchPath(ROUTES.TASK_PROJECT_PAGE, pathname)

const hasDetailFor = (state: IRootState, ref: IAtlasObjectRef): boolean => {
  const preview = state.atlasPreview
  if (!preview?.target || atlasObjectRefKey(preview.target.ref) !== atlasObjectRefKey(ref)) {
    return false
  }
  return preview.isLoading || !!preview.detail
}

/** The card says "no access" for these; swapping them into the panel would lose the open record. */
const isKnownRestricted = (state: IRootState, ref: IAtlasObjectRef): boolean =>
  atlasObjectPreviews(state).byKey[atlasObjectRefKey(ref)]?.restricted === true

const isSameRecord = (a: IAtlasPreviewTarget, b: IAtlasPreviewTarget): boolean =>
  atlasObjectRefKey(a.ref) === atlasObjectRefKey(b.ref) ||
  toAtlasRouterPath(a.url) === toAtlasRouterPath(b.url)

interface IOpenOptions {
  isPinned?: boolean
}

/**
 * Opens an Atlas record without leaving the page, in a card hanging off `anchor`. Clicking a
 * task on a page that already hosts the task panel opens it there instead, keeping the
 * page's filters; clicking a record while AIM's record panel is open swaps it into the panel,
 * unless the link is known to be restricted, which keeps the card and its "no access" message.
 * Hovering only shows the card; it never moves to either panel.
 */
export const useOpenAtlasPreview = () => {
  const dispatch = useDispatch()
  const store = useStore<IRootState>()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { canOpenInPanel, openInPanel } = useAtlasModulePanel()

  return useCallback(
    (
      ref: IAtlasObjectRef,
      url: string,
      anchor: HTMLElement,
      { isPinned = true }: IOpenOptions = {},
    ) => {
      if (isPinned && ref.type === AtlasObjectType.Task && isTaskPanelHost(pathname)) {
        const params = new URLSearchParams(search)
        params.set('taskId', ref.id)
        void navigate({ pathname, search: params.toString() })
        return
      }

      const panelTarget =
        isPinned && canOpenInPanel ? atlasModulePanelTarget(store.getState()) : null
      if (panelTarget && !isKnownRestricted(store.getState(), ref)) {
        const target = { ref, url }
        if (isSameRecord(panelTarget, target)) return
        clearPreviewAnchor()
        dispatch(closeAtlasPreview())
        openInPanel(target)
        return
      }

      const isAlreadyLoaded = hasDetailFor(store.getState(), ref)
      anchorPreview(anchor, isPinned)
      dispatch(openAtlasPreview({ ref, url }))
      if (!isAlreadyLoaded) {
        void dispatch(getAtlasObjectDetail(ref))
      }
    },
    [canOpenInPanel, dispatch, navigate, openInPanel, pathname, search, store],
  )
}
